import { memo } from 'react';
import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { Grid } from '@mui/material';
import { useGetDimensionalityOptions } from 'constants/businessTool';
import { FOR_INVESTORS_SECTIONS, FOR_INVESTORS_SUB_SECTIONS } from 'constants/businessToolSections/forInvestors';

import useCheckSubSectionAccess from 'hooks/useCheckSubSectionAccess';

import { FieldArrayComponent, FieldArrayTabs } from 'components/_basic/Form/FieldArray';
import { TextInputControl } from 'components/_basic/Form/TextInput';
import { FormHeader } from 'components/_basic/FormHeader/FormHeader';
import { FormSectionCard } from 'components/_basic/FormSectionCard/FormSectionCard';
import { FORM_SECTION_CARD_VARIANT } from 'components/_basic/FormSectionCard/types';
import { FormSectionFilledCard } from 'components/_basic/FormSectionFilledCard';
import { CurrencySelector } from 'components/BusinessToolForm/components/CurrencySelector';
import { AveragePricesTable } from 'components/BusinessToolForm/Sections/MarketAnalysis/MarketAnalysisOverview/components/AveragePricesTable';
import { CurrentMarketStructure } from 'components/BusinessToolForm/Sections/MarketAnalysis/MarketAnalysisOverview/components/CurrentMarketStructure';
import { ForecastedConsumption } from 'components/BusinessToolForm/Sections/MarketAnalysis/MarketAnalysisOverview/components/ForecastedConsumption';
import { MarketTrendRow } from 'components/BusinessToolForm/Sections/MarketAnalysis/MarketAnalysisOverview/components/MarketTrendRow';
import { MonthYearDatepicker } from 'components/BusinessToolForm/Sections/MarketAnalysis/MarketAnalysisOverview/components/MonthYearDatePicker';
import {
  CURRENT_MARKET_STRUCTURE_NAMES,
  FORECASTED_CONSUMPTION_CHART_NAMES,
  forecastedConsumptionChartInitialValues,
  MARKET_ANALYSIS_OVERVIEW_NAMES,
  marketTrendsInitialValues,
  prefix,
} from 'components/BusinessToolForm/Sections/MarketAnalysis/MarketAnalysisOverview/utils/form';

import { concatWithDot } from 'helpers/string';

export const MarketOverview: React.FC = memo(() => {
  const { t } = useTranslation();
  const { control } = useFormContext();
  const DIMENSIONALITY_OPTIONS = useGetDimensionalityOptions();

  const canAccess = useCheckSubSectionAccess(
    FOR_INVESTORS_SECTIONS.MARKET_ANALYSIS,
    FOR_INVESTORS_SUB_SECTIONS.FOR_INVESTORS_MARKET_ANALYSIS.MARKET_OVERVIEW,
  );

  return (
    <>
      <FormSectionCard variant={FORM_SECTION_CARD_VARIANT.LARGE}>
        <FormHeader
          locked={!canAccess}
          title={t('marketAnalysisForm.title')}
          badgeText={t('marketAnalysisForm.overview.title')}
          section={FOR_INVESTORS_SECTIONS.MARKET_ANALYSIS}
        />
      </FormSectionCard>
      {/* 4.1.1 */}
      <FormSectionCard
        disabled={!canAccess}
        title={t('marketAnalysisForm.overview.mainMarketOverview')}
        aiFieldName={concatWithDot(prefix, MARKET_ANALYSIS_OVERVIEW_NAMES.MAIN_MARKET_OVERVIEW)}
      >
        <TextInputControl
          multiline
          control={control}
          id="marketAnalysisForm.overview.mainMarketOverview"
          name={concatWithDot(prefix, MARKET_ANALYSIS_OVERVIEW_NAMES.MAIN_MARKET_OVERVIEW)}
          minRows={3}
          disabled={!canAccess}
          maxLength={1500}
        />
      </FormSectionCard>
      {/* 4.1.2 */}
      <FormSectionCard
        disabled={!canAccess}
        title={t('marketAnalysisForm.overview.servicesProductsSummary')}
        aiFieldName={concatWithDot(prefix, MARKET_ANALYSIS_OVERVIEW_NAMES.SERVICES_PRODUCTS_SUMMARY)}
      >
        <TextInputControl
          multiline
          control={control}
          id="marketAnalysisForm.overview.servicesProductsSummary"
          name={concatWithDot(prefix, MARKET_ANALYSIS_OVERVIEW_NAMES.SERVICES_PRODUCTS_SUMMARY)}
          minRows={3}
          disabled={!canAccess}
          maxLength={1500}
        />
      </FormSectionCard>
      {/* 4.1.5 */}
      <FormSectionCard
        disabled={!canAccess}
        title={t('marketAnalysisForm.overview.consumptionDetails')}
        aiFieldName={concatWithDot(prefix, MARKET_ANALYSIS_OVERVIEW_NAMES.ACTUAL_AND_FORECASTED_CONSUMPTION)}
      >
        <TextInputControl
          multiline
          control={control}
          id="marketAnalysisForm.overview.consumptionDetails"
          name={concatWithDot(prefix, MARKET_ANALYSIS_OVERVIEW_NAMES.ACTUAL_AND_FORECASTED_CONSUMPTION)}
          minRows={3}
          disabled={!canAccess}
          maxLength={1500}
        />
      </FormSectionCard>
      {/* 4.1.6 */}
      <FieldArrayTabs
        name={concatWithDot(prefix, MARKET_ANALYSIS_OVERVIEW_NAMES.FORECASTED_CONSUMPTION_CHART)}
        buttonItemName={FORECASTED_CONSUMPTION_CHART_NAMES.TITLE}
        title={t('marketAnalysisForm.overview.marketTrends.title')}
        buttonText={t('chart')}
        removeButtonText={t('removeChart')}
        initialItem={{...forecastedConsumptionChartInitialValues,  [FORECASTED_CONSUMPTION_CHART_NAMES.TITLE]: t('chart'),}}
        disabled={!canAccess}
      >
        {(namePrefix) => <ForecastedConsumption name={namePrefix} disabled={!canAccess} />}
      </FieldArrayTabs>
      {/* 4.1.8 */}
      <FormSectionCard
        disabled={!canAccess}
        title={t('marketAnalysisForm.overview.marketTrends.title')}
        aiFieldName={concatWithDot(prefix, MARKET_ANALYSIS_OVERVIEW_NAMES.MARKET_TRENDS)}
        aiFieldType="complex"
        collapsible
      >
        <FieldArrayComponent
          name={concatWithDot(prefix, MARKET_ANALYSIS_OVERVIEW_NAMES.MARKET_TRENDS)}
          initialItem={marketTrendsInitialValues}
          buttonText={t('trend')}
          disabled={!canAccess}
        >
          {(namePrefix) => <MarketTrendRow name={namePrefix} disabled={!canAccess} />}
        </FieldArrayComponent>
      </FormSectionCard>
      {/* 4.1.9 */}
      <FormSectionCard
        disabled={!canAccess}
        title={t('marketAnalysisForm.overview.salesMethod')}
        aiFieldName={concatWithDot(prefix, MARKET_ANALYSIS_OVERVIEW_NAMES.SALES_METHOD)}
      >
        <TextInputControl
          multiline
          control={control}
          id="marketAnalysisForm.overview.salesMethod"
          name={concatWithDot(prefix, MARKET_ANALYSIS_OVERVIEW_NAMES.SALES_METHOD)}
          minRows={3}
          disabled={!canAccess}
          maxLength={1500}
        />
      </FormSectionCard>
      {/* 4.1.10 */}
      <FormSectionCard
        disabled={!canAccess}
        title={t('marketAnalysisForm.overview.marketStructureDescription')}
        aiFieldName={concatWithDot(prefix, MARKET_ANALYSIS_OVERVIEW_NAMES.MARKET_STRUCTURE_DESCRIPTION)}
      >
        <TextInputControl
          multiline
          control={control}
          id="marketAnalysisForm.overview.marketStructureDescription"
          name={concatWithDot(prefix, MARKET_ANALYSIS_OVERVIEW_NAMES.MARKET_STRUCTURE_DESCRIPTION)}
          minRows={3}
          disabled={!canAccess}
          maxLength={1500}
        />
      </FormSectionCard>
      {/* 4.1.11 */}
      <FieldArrayTabs
        name={concatWithDot(prefix, MARKET_ANALYSIS_OVERVIEW_NAMES.CURRENT_MARKET_STRUCTURE)}
        buttonItemName={CURRENT_MARKET_STRUCTURE_NAMES.TITLE}
        title={t('marketAnalysisForm.overview.indicateMarketStructure')}
        buttonText={t('chart')}
        removeButtonText={t('removeChart')}
        initialItem={{...forecastedConsumptionChartInitialValues,  [FORECASTED_CONSUMPTION_CHART_NAMES.TITLE]: t('chart'),}}
        disabled={!canAccess}
      >
        {(namePrefix) => <CurrentMarketStructure name={namePrefix} disabled={!canAccess} />}
      </FieldArrayTabs>
      {/* 4.1.12 */}
      <FormSectionCard
        disabled={!canAccess}
        title={t('marketAnalysisForm.overview.marketGrowthFactors')}
        aiFieldName={concatWithDot(prefix, MARKET_ANALYSIS_OVERVIEW_NAMES.MARKET_GROWTH_FACTORS)}
      >
        <TextInputControl
          multiline
          control={control}
          id="marketAnalysisForm.overview.marketGrowthFactors"
          name={concatWithDot(prefix, MARKET_ANALYSIS_OVERVIEW_NAMES.MARKET_GROWTH_FACTORS)}
          minRows={3}
          disabled={!canAccess}
          maxLength={1500}
        />
      </FormSectionCard>
      {/* 4.1.14 */}
      <FormSectionCard
        disabled={!canAccess}
        title={t('marketAnalysisForm.overview.priceStrategies')}
        aiFieldName={concatWithDot(prefix, MARKET_ANALYSIS_OVERVIEW_NAMES.PRICE_STRATEGIES)}
      >
        <TextInputControl
          multiline
          control={control}
          id="marketAnalysisForm.overview.priceStrategies"
          name={concatWithDot(prefix, MARKET_ANALYSIS_OVERVIEW_NAMES.PRICE_STRATEGIES)}
          minRows={3}
          disabled={!canAccess}
          maxLength={1500}
        />
      </FormSectionCard>
      {/* 4.1.15 */}
      <FormSectionCard disabled={!canAccess} title={t('marketAnalysisForm.overview.averagePrices')} collapsible>
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <TextInputControl
              control={control}
              id="marketAnalysisForm.overview.priceStrategies"
              name={concatWithDot(prefix, MARKET_ANALYSIS_OVERVIEW_NAMES.AVERAGE_PRICES_TITLE)}
              label={t('chartTitle')}
              disabled={!canAccess}
            />
          </Grid>
          <Grid container justifyContent="space-between" alignItems="center" item>
            <MonthYearDatepicker
              name={concatWithDot(prefix, MARKET_ANALYSIS_OVERVIEW_NAMES.AVERAGE_PRICES_DATE)}
              disabled={!canAccess}
            />
            <CurrencySelector
              name={concatWithDot(prefix, MARKET_ANALYSIS_OVERVIEW_NAMES.AVERAGE_PRICES_CURRENCY)}
              defaultValue={DIMENSIONALITY_OPTIONS[0].value}
              disabled={!canAccess}
            />
          </Grid>
          <Grid item xs={12}>
            <FormSectionFilledCard>
              <AveragePricesTable
                name={concatWithDot(prefix, MARKET_ANALYSIS_OVERVIEW_NAMES.AVERAGE_PRICES_CHART)}
                disabled={!canAccess}
              />
            </FormSectionFilledCard>
          </Grid>
        </Grid>
      </FormSectionCard>
    </>
  );
});
